<template>
  <form @submit="onSubmit">
    <BaseCard class="relative mx-auto w-full max-w-sm">
      <div class="mb-3 space-y-2">
        <FormInputText name="email" label="Email" />
        <FormInputText
          name="firstName"
          label="First Name"
        />
        <FormInputText name="lastName" label="Last Name" />
      </div>
      <FormSubmitButton
        v-if="!isRetrievingUser"
        :is-submitting="isSubmitting"
        class="w-full"
      >
        Update User
      </FormSubmitButton>

      <BaseInnerLoading v-if="userId && isRetrievingUser" />
    </BaseCard>
  </form>

  <ErrorModal
    :is-open="isErrorModalOpen"
    message="Could not retrieve user"
    description="Something went wrong fetching user details"
    :go-back-to="{
      name: 'merchantGroupUsersIndex',
      params: {},
    }"
    go-back-label="Return to user list"
  />
</template>

<script setup lang="ts">
import {
  type CreateUpdateUserFormOutput,
  useCreateUpdateUserForm,
} from "@/composables/forms/useCreateUpdateUserForm"
import { ApiError } from "@/api"

const emit = defineEmits<{
  (
    event: "submit",
    values: CreateUpdateUserFormOutput
  ): void
}>()

const props = defineProps<{
  userId: string
}>()

const router = useRouter()

const { handleSubmit, setErrors, setValues, isSubmitting } =
  useCreateUpdateUserForm()

const isRetrievingUser = ref(false)
const isErrorModalOpen = ref(false)

onMounted(async () => {
  if (props.userId !== undefined) {
    try {
      isRetrievingUser.value = true
      const user = await retrieveMerchantGroupUser(
        props.userId
      )
      setValues({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      })
    } catch {
      isErrorModalOpen.value = true
    } finally {
      isRetrievingUser.value = false
    }
  }
})

const onSubmit = handleSubmit(async (formValues) => {
  try {
    await updateMerchantGroupUser(props.userId, {
      ...formValues,
    })
    toast.success("User updated")
    router.push({ name: "merchantGroupUsersIndex" })
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    }
    toast.error("Failed to update user")
    throw error
  }
})
</script>
